import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLoadingBar } from './store/uiSlice';

type AppLoaderProps = {
    color?: string;
    height?: number;
    className?: string;
};

function AppLoader({
    color = 'bg-ata-purple/50',
    height = 3,
    className = '',
}: AppLoaderProps) {
    const { show: isLoading, progress } = useSelector(getLoadingBar);
    const [internalProgress, setInternalProgress] = useState(0);

    useEffect(() => {
        if (!isLoading) {
            setInternalProgress(0);
            return;
        }

        // If explicit progress is provided, use it
        if (progress > 0) {
            setInternalProgress(progress);
            return;
        }

        // Otherwise, simulate progress
        const interval = setInterval(() => {
            setInternalProgress(prev => {
                // Slow down as we approach 100%
                const increment = prev < 30 ? 10 : prev < 60 ? 5 : prev < 90 ? 2 : 0.5;
                return Math.min(prev + increment, 95); // Stop at 95% until loading completes
            });
        }, 100);

        return () => clearInterval(interval);
    }, [isLoading, progress]);

    // Complete the bar when loading finishes
    useEffect(() => {
        if (!isLoading && internalProgress > 0) {
            setInternalProgress(100);
            // Reset after animation completes
            const timeout = setTimeout(() => setInternalProgress(0), 300);
            return () => clearTimeout(timeout);
        }
    }, [isLoading, internalProgress]);

    if (internalProgress === 0) return null;

    return (
        <div
            className={`fixed top-0 left-0 right-0 z-50 ${className}`}
            style={{ height }}
        >
            <motion.div
                className={`h-full ${color} shadow-lg rounded-md`}
                initial={{ width: 0 }}
                animate={{ width: `${internalProgress}%` }}
                transition={{
                    duration: 0.3,
                    ease: "easeOut"
                }}
            />
            <motion.div
                className={`absolute top-0 right-0 h-full w-20 ${color} opacity-60 blur-sm`}
                initial={{ opacity: 0 }}
                animate={{ opacity: internalProgress > 5 ? 0.6 : 0 }}
                transition={{ duration: 0.2 }}
            />
        </div>
    );
}

export default AppLoader;